import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { stats } from '@/mocks/videos';


export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '一起草在线观看',
    url: 'https://wgecino.cn',
    description: '一起草在线观看是一个免费高清视频播放平台，提供海量高清视频资源，无需下载即可流畅播放。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@wgecino.cn',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://wgecino.cn', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': 'https://wgecino.cn/about', name: '关于我们' } },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="px-4 md:px-6 lg:px-12">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <section className="py-8">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-2">
                  关于一起草在线观看
                </h1>
                <p className="text-sm text-foreground-500 mb-8">
                  免费高清视频播放平台，致力于为用户提供最优质的在线视频观看体验
                </p>

                {/* Stats Bar */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                  {stats.map((stat, i) => (
                    <div key={i} className="text-center p-5 rounded-lg bg-background-100/70">
                      <div className="text-xl md:text-2xl font-bold text-primary-500 font-heading">{stat.value}</div>
                      <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>

                {/* Tab Navigation */}
                <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-10 w-fit">
                  {[
                    { key: 'intro', label: '品牌介绍' },
                    { key: 'reputation', label: '品牌信誉' },
                    { key: 'contact', label: '联系我们' },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key as typeof activeTab)}
                      className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                        activeTab === tab.key
                          ? 'bg-background-50 text-foreground-950'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <div className="min-h-[300px]">
                  {activeTab === 'intro' && (
                    <div className="space-y-8">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                        <div>
                          <h2 className="text-lg md:text-2xl font-bold text-foreground-950 font-heading mb-4">
                            关于我们
                          </h2>
                          <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                            一起草在线观看成立于2020年，是国内领先的免费高清视频播放平台。我们始终秉持「让每个人都能轻松享受优质视频内容」的理念，致力于打造一个开放、便捷、高品质的视频观看平台。
                          </p>
                          <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                            经过多年的发展，一起草在线观看已积累了超过<strong>10万部</strong>高清视频资源，涵盖电视剧、电影、综艺、动漫、纪录片、音乐等多个品类。我们的月活跃用户已突破<strong>5000万</strong>，成为国内最受欢迎的视频平台之一。
                          </p>
                          <p className="text-sm text-foreground-600 leading-relaxed">
                            我们拥有一支专业的内容运营和技术研发团队，持续优化平台体验，确保每一位用户都能享受到流畅、高清、稳定的观看服务。
                          </p>
                        </div>
                        <div className="rounded-lg overflow-hidden">
                          <img
                            src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20workspace%20with%20large%20screens%20displaying%20video%20content%2C%20team%20collaboration%20atmosphere%2C%20warm%20natural%20lighting%20through%20large%20windows%2C%20professional%20digital%20media%20company%20environment%2C%20clean%20and%20minimal%20design&width=800&height=500&seq=about-intro-2026&orientation=landscape"
                            alt="一起草在线观看团队办公环境"
                            title="一起草在线观看 - 办公环境"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>

                      <div>
                        <h3 className="text-base md:text-lg font-bold text-foreground-950 font-heading mb-4">
                          我们的使命
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {[
                            { icon: 'ri-heart-line', title: '让观影更简单', desc: '无需注册，无需付费，打开即看。我们相信优质的视频内容应该向所有人开放。' },
                            { icon: 'ri-shield-check-line', title: '品质有保障', desc: '严格的内容筛选机制，确保每部视频都符合高品质标准，让用户看得放心。' },
                            { icon: 'ri-rocket-line', title: '技术驱动体验', desc: '持续投入技术研发，CDN加速、智能推荐，只为提供极致的观看体验。' },
                          ].map((item, i) => (
                            <div key={i} className="p-5 rounded-lg bg-background-100/70">
                              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mb-3">
                                <i className={`${item.icon} text-lg text-accent-600`}></i>
                              </div>
                              <h4 className="text-sm font-semibold text-foreground-900 mb-2">{item.title}</h4>
                              <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'reputation' && (
                    <div className="space-y-8">
                      <div>
                        <h2 className="text-lg md:text-2xl font-bold text-foreground-950 font-heading mb-4">
                          品牌信誉
                        </h2>
                        <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                          一起草在线观看凭借优质的服务和良好的用户口碑，赢得了广泛的行业认可和用户信赖。以下是我们的核心信誉保障：
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {[
                          {
                            icon: 'ri-award-line',
                            title: '行业认可',
                            desc: '连续三年荣获「最佳视频平台」称号，获得行业协会多项嘉奖。我们始终坚持合规运营，为用户提供安全可靠的视频服务。',
                          },
                          {
                            icon: 'ri-user-heart-line',
                            title: '用户信赖',
                            desc: '累计服务超过5000万用户，用户满意度评分高达4.9分。真实用户口碑是我们最宝贵的财富。',
                          },
                          {
                            icon: 'ri-lock-line',
                            title: '数据安全',
                            desc: '严格执行数据保护政策，用户隐私安全是我们的底线。采用行业领先的加密技术保障数据传输安全。',
                          },
                          {
                            icon: 'ri-customer-service-2-line',
                            title: '贴心服务',
                            desc: '7×24小时在线客服支持，快速响应用户反馈。我们认真对待每一条建议，持续改进服务品质。',
                          },
                        ].map((item, i) => (
                          <div key={i} className="flex gap-4 p-5 rounded-lg bg-background-100/70">
                            <div className="w-12 h-12 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                              <i className={`${item.icon} text-xl text-secondary-600`}></i>
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-foreground-900 mb-2">{item.title}</h4>
                              <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'contact' && (
                    <div className="space-y-8">
                      <div>
                        <h2 className="text-lg md:text-2xl font-bold text-foreground-950 font-heading mb-4">
                          联系我们
                        </h2>
                        <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                          如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。我们非常乐意听取您的声音。
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="space-y-5">
                          {[
                            { icon: 'ri-mail-line', label: '电子邮箱', value: 'support@wgecino.cn' },
                            { icon: 'ri-customer-service-line', label: '在线客服', value: '7×24小时在线服务' },
                            { icon: 'ri-time-line', label: '响应时间', value: '工作日24小时内回复' },
                            { icon: 'ri-global-line', label: '官方网站', value: 'https://wgecino.cn' },
                          ].map((item, i) => (
                            <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-background-100/70">
                              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                <i className={`${item.icon} text-lg text-primary-500`}></i>
                              </div>
                              <div>
                                <div className="text-xs text-foreground-400 mb-0.5">{item.label}</div>
                                <div className="text-sm font-medium text-foreground-900">{item.value}</div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="bg-background-100/70 rounded-lg p-6">
                          <h3 className="text-base font-semibold text-foreground-900 font-heading mb-4">
                            给我们留言
                          </h3>
                          <form id="contact-form" data-readdy-form="" className="space-y-4" method="post" action="https://readdy.ai/api/form/d9u11vtca23r0tr1aoe0">
                            <div>
                              <label htmlFor="contact_name" className="block text-xs font-medium text-foreground-600 mb-1.5">您的姓名</label>
                              <input
                                type="text"
                                id="contact_name"
                                name="name"
                                required
                                className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-300"
                                placeholder="请输入您的姓名"
                              />
                            </div>
                            <div>
                              <label htmlFor="contact_email" className="block text-xs font-medium text-foreground-600 mb-1.5">电子邮箱</label>
                              <input
                                type="email"
                                id="contact_email"
                                name="email"
                                required
                                className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-300"
                                placeholder="请输入您的邮箱"
                              />
                            </div>
                            <div>
                              <label htmlFor="contact_message" className="block text-xs font-medium text-foreground-600 mb-1.5">留言内容</label>
                              <textarea
                                id="contact_message"
                                name="message"
                                required
                                maxLength={500}
                                rows={4}
                                className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-300 resize-none"
                                placeholder="请输入您的留言（最多500字）"
                              ></textarea>
                            </div>
                            <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
                            <button
                              type="submit"
                              className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2.5 rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
                            >
                              提交留言
                            </button>
                          </form>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}